import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import {
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  linkWithCredential,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type AuthCredential,
  type User,
} from 'firebase/auth'
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '@/lib/firebase'
import { sendVerificationEmail, sendWelcomeEmail } from '@/lib/callables'
import { useAuthStore } from '@/stores/auth-store'
import { Spinner } from '@/components/ui/spinner'
import type { UserProfile } from '@/types/models'

/** Keep the overlay visible long enough to paint and read. */
const SIGN_OUT_MIN_MS = 3000

type PendingLink = {
  credential: AuthCredential
  email: string
}

type AuthContextValue = {
  user: User | null
  profile: UserProfile | null
  isLoading: boolean
  isGuest: boolean
  isAuthenticated: boolean
  pendingLink: PendingLink | null
  setPendingLink: (link: PendingLink | null) => void
  signInWithGoogle: () => Promise<void>
  signInWithApple: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signUpWithEmail: (name: string, email: string, password: string) => Promise<void>
  continueAsGuest: () => void
  logout: () => Promise<void>
  isSigningOut: boolean
  linkPendingCredential: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function toProfile(uid: string, data: Record<string, unknown>, emailVerified: boolean): UserProfile {
  return {
    id: uid,
    email: String(data.email ?? ''),
    displayName: data.displayName != null ? String(data.displayName) : null,
    photoURL: data.photoURL != null ? String(data.photoURL) : null,
    favProperties: Array.isArray(data.favProperties) ? (data.favProperties as string[]) : [],
    sentBiddles: Array.isArray(data.sentBiddles) ? (data.sentBiddles as string[]) : [],
    receivedBiddles: Array.isArray(data.receivedBiddles)
      ? (data.receivedBiddles as string[])
      : [],
    letterCredits: typeof data.letterCredits === 'number' ? data.letterCredits : 0,
    isIdentityVerified: Boolean(data.isIdentityVerified),
    verificationMethod: data.verificationMethod != null ? String(data.verificationMethod) : null,
    emailVerified,
  }
}

async function ensureUserDoc(user: User) {
  const ref = doc(db, 'users', user.uid)
  await setDoc(
    ref,
    {
      email: user.email ?? '',
      displayName: user.displayName ?? null,
      photoURL: user.photoURL?.toString() ?? null,
      lastSignInAt: serverTimestamp(),
      termsVersion: '2026-05-22',
      privacyVersion: '2026-05-22',
    },
    { merge: true },
  )
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const {
    firebaseUser,
    profile,
    isLoading,
    isGuest,
    setFirebaseUser,
    setProfile,
    setLoading,
    continueAsGuest,
    reset,
  } = useAuthStore()

  const [pendingLink, setPendingLink] = useState<PendingLink | null>(null)
  const [isSigningOut, setIsSigningOut] = useState(false)

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user)
      if (!user) {
        setProfile(null)
        setLoading(false)
        return
      }
      try {
        const isNew =
          user.metadata.creationTime != null &&
          user.metadata.lastSignInTime != null &&
          user.metadata.creationTime === user.metadata.lastSignInTime
        await ensureUserDoc(user)
        if (isNew) {
          try {
            await sendWelcomeEmail()
          } catch {
            // Non-fatal
          }
        }
      } finally {
        setLoading(false)
      }
    })
    return () => unsub()
  }, [setFirebaseUser, setProfile, setLoading])

  useEffect(() => {
    if (!firebaseUser) {
      setProfile(null)
      return
    }
    const unsub = onSnapshot(doc(db, 'users', firebaseUser.uid), (snap) => {
      if (!snap.exists()) return
      setProfile(toProfile(firebaseUser.uid, snap.data(), firebaseUser.emailVerified))
    })
    return () => unsub()
  }, [firebaseUser, setProfile])

  // After signing in with the existing provider, link the parked credential.
  useEffect(() => {
    if (!firebaseUser || !pendingLink) return
    let cancelled = false
    ;(async () => {
      try {
        await linkWithCredential(firebaseUser, pendingLink.credential)
        if (!cancelled) setPendingLink(null)
      } catch {
        // Leave pendingLink so the UI can retry / explain.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [firebaseUser, pendingLink])

  const handleCredentialError = useCallback(async (error: unknown) => {
    const e = error as {
      code?: string
      customData?: { email?: string }
      credential?: AuthCredential
    }
    if (e.code === 'auth/account-exists-with-different-credential' && e.customData?.email) {
      const credential =
        e.credential ??
        GoogleAuthProvider.credentialFromError(error as never) ??
        OAuthProvider.credentialFromError(error as never)
      if (credential) {
        setPendingLink({ credential, email: e.customData.email })
      }
    }
    throw error
  }, [])

  const signInWithGoogle = useCallback(async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider())
    } catch (error) {
      await handleCredentialError(error)
    }
  }, [handleCredentialError])

  const signInWithApple = useCallback(async () => {
    try {
      const provider = new OAuthProvider('apple.com')
      provider.addScope('email')
      provider.addScope('name')
      await signInWithPopup(auth, provider)
    } catch (error) {
      await handleCredentialError(error)
    }
  }, [handleCredentialError])

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password)
  }, [])

  const signUpWithEmail = useCallback(async (name: string, email: string, password: string) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password)
    if (name.trim()) {
      await updateProfile(cred.user, { displayName: name.trim() })
    }
    try {
      await sendVerificationEmail()
    } catch {
      // Non-fatal — user can resend from Settings / Verify email.
    }
  }, [])

  const logout = useCallback(async () => {
    setIsSigningOut(true)
    const started = Date.now()
    try {
      await signOut(auth)
      reset()
      const remaining = SIGN_OUT_MIN_MS - (Date.now() - started)
      if (remaining > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remaining))
      }
    } finally {
      setIsSigningOut(false)
    }
  }, [reset])

  const linkPendingCredential = useCallback(async () => {
    if (!auth.currentUser || !pendingLink) return
    await linkWithCredential(auth.currentUser, pendingLink.credential)
    setPendingLink(null)
  }, [pendingLink])

  const refreshUser = useCallback(async () => {
    if (!auth.currentUser) return
    await auth.currentUser.reload()
    setFirebaseUser(auth.currentUser)
  }, [setFirebaseUser])

  const value = useMemo<AuthContextValue>(
    () => ({
      user: firebaseUser,
      profile,
      isLoading,
      isGuest,
      isAuthenticated: Boolean(firebaseUser) || isGuest,
      pendingLink,
      setPendingLink,
      signInWithGoogle,
      signInWithApple,
      signInWithEmail,
      signUpWithEmail,
      continueAsGuest,
      logout,
      isSigningOut,
      linkPendingCredential,
      refreshUser,
    }),
    [
      firebaseUser,
      profile,
      isLoading,
      isGuest,
      pendingLink,
      signInWithGoogle,
      signInWithApple,
      signInWithEmail,
      signUpWithEmail,
      continueAsGuest,
      logout,
      isSigningOut,
      linkPendingCredential,
      refreshUser,
    ],
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
      {isSigningOut
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-3 bg-black/70 backdrop-blur-sm"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <Spinner className="h-8 w-8 border-white/30 border-t-white" />
              <p className="text-sm font-medium text-white">Signing out…</p>
            </div>,
            document.body,
          )
        : null}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
